import {
  ApprovalAction,
  ApprovalPolicyCondition,
  ApprovalTerritoryCondition,
  BookingFeeOptions,
  ClientActivationStatus,
  ClientApprovalConfigType,
  ClientBookingPhoneConfig,
  TravelIntegrationOption,
} from "~/apps/shared/constants/enums";
import { logger } from "~/apps/shared/utils/logger";
import { Descendant } from "slate";
import {
  ConfigOptions,
  FieldVisibility,
  Nullable,
  AmenitiesPreference,
  InsuranceSelectionPreference,
} from "sm-types/common";

import { BillingProfileSelection, CostCenterSelection } from "@constants/enums";

import {
  ApprovalPaySelection,
  AreaCcEnablingStatus,
  CorpIndividualCards,
  CorpIndividualSettingsStatus,
  FeatureVisibilities,
  PostRequest,
  PreRequest,
  VPRegisterStatus,
} from "../dtos/client.dto";
import { CarSupplier } from "./car.model";

export type Client = {
  activationStatus: ClientActivationStatus;
  budgetsActive: boolean;
  clientSlug: string;
  clientToken: string;
  costCenterActive: boolean;
  createdAt: string;
  email: string;
  id: string;
  name: string;
};

export type ClientBoss = {
  email: string;
  employeeToken: string;
  token: string;
};

export type ApprovalConfig = {
  type: ClientApprovalConfigType;
  ruleToApply: Nullable<{
    action: ApprovalPolicyCondition;
    policyCondition: ApprovalTerritoryCondition;
    territoryCondition: ApprovalAction;
  }>;
};

export type ClientConfig = {
  approvalConfig: ApprovalConfig;
  approvalPaySelection: ApprovalPaySelection;
  approvalPriorityRanking?: string;
  bank3Enable?: boolean;
  bookingPhoneConfig: ClientBookingPhoneConfig;
  budgetsActive: boolean;
  clientConfigToken: string;
  companyAreaDisplay: string;
  companyAreaEnabled: boolean;
  companyBoardDisplay: string;
  config: ConfigOptions;
  costCenterActive: boolean;
  customCorporativeCardName: string;
  customMessage?: string;
  customMessageLink?: string;
  featureVisibilities: FeatureVisibilities;
  corpIndividualCards: CorpIndividualCards;
  guestApprovalType: GuestApprovalType;
  projectEnablingState: "off" | "optional" | "required";
  supportChannel: string;
  tagsAdminOnly: boolean;
  travelIntegration: TravelIntegrationOption;
  travelTagsRequired: boolean;
  agencyEmail: string;
  vpRegisterStatus: VPRegisterStatus;
  areaCcEnablingStatus: AreaCcEnablingStatus;
  billingProfileSelection: BillingProfileSelection;
  costCenterSelection: CostCenterSelection;
};

export class ClientConfigModel {
  constructor(protected clientConfig: ClientConfig) {}

  public changeClientConfig(clientConfig: Partial<ClientConfig>) {
    this.clientConfig = {
      ...this.clientConfig,
      ...clientConfig,
    };

    return this;
  }

  public hasMandatoryProtections(supplier: string): boolean {
    if (supplier === CarSupplier.MOVIDA) {
      return (
        this.clientConfig.config.cars?.movida_additional_preferences?.insurance
          ?.selection === InsuranceSelectionPreference.REQUIRED
      );
    }

    if (supplier === CarSupplier.LOCALIZA) {
      return (
        this.clientConfig.config.cars?.localiza_additional_preferences
          ?.insurance?.selection === InsuranceSelectionPreference.REQUIRED
      );
    }

    return false;
  }

  public isGuestApprovalTypeBookerFlow(): boolean {
    return (
      this.clientConfig.guestApprovalType === GuestApprovalType.BOOKER_FLOW
    );
  }

  public isSingleApprovalFlow(): boolean {
    return this.isBossApprovalFlow() || this.isNavanApprovalFlow();
  }

  public isTripPurposeEnabled() {
    return (
      this.clientConfig.config.travels.tripPurpose ===
        FieldVisibility.OPTIONAL ||
      this.clientConfig.config.travels.tripPurpose === FieldVisibility.REQUIRED
    );
  }

  public vpRegisterStatusIsEnabled() {
    return this.clientConfig.vpRegisterStatus === VPRegisterStatus.ENABLED;
  }

  public areaCcEnablingStatusIsEnabled() {
    return (
      this.clientConfig.areaCcEnablingStatus === AreaCcEnablingStatus.ENABLED
    );
  }

  public getBillingProfileSelection() {
    return this.clientConfig.billingProfileSelection;
  }

  public isBillingProfileSelectionAll() {
    return (
      this.clientConfig.billingProfileSelection === BillingProfileSelection.ALL
    );
  }

  public isBillingProfileSelectionAllIfUserUnassociated() {
    return (
      this.clientConfig.billingProfileSelection ===
      BillingProfileSelection.ALL_IF_USER_UNASSOCIATED
    );
  }

  public getCostCenterSelection() {
    return this.clientConfig.costCenterSelection;
  }

  public isCostCenterSelectionCnpjAssociatedOnly() {
    return (
      this.clientConfig.costCenterSelection ===
      CostCenterSelection.CNPJ_ASSOCIATED_ONLY
    );
  }

  public getClientTravelExigencies() {
    return {
      company_area_enabled: this.isCompanyAreaEnabled(),
      cost_center_active: this.isCompanyCostCenterEnabled(),
      project_enabling_state: this.getProjectEnablingState(),
      booking_phone_config: this.getBookingPhoneConfig(),
      billing_profile_selection: this.getBillingProfileSelection(),
      config: {
        travels: {
          justificationPolicy: this.getJustificationPolicy(),
          tripPurpose: this.getTripPurpose(),
          travelerSelection: this.getTravelerSelection(),
        },
        flights: {
          samePlaneLimit: this.getSamePlaneLimit(),
        },
        hotels: {
          amenities_preferences: this.getHotelAmenitiesPreferences(),
        },
      },
    };
  }

  public getHotelAmenitiesPreferences() {
    const defaultPreferences = {
      breakfast: AmenitiesPreference.NONE,
      refund: AmenitiesPreference.NONE,
    };

    const hotels = this.clientConfig.config.hotels;

    const amenitiesPreferences = hotels && hotels.amenities_preferences;

    if (amenitiesPreferences) {
      return amenitiesPreferences;
    }

    return defaultPreferences;
  }

  public getTravelerSelection() {
    return this.clientConfig.config.travels.travelerSelection;
  }

  public getSamePlaneLimit() {
    return this.clientConfig.config.flights.samePlaneLimit;
  }

  public getTripPurpose() {
    return this.clientConfig.config.travels.tripPurpose;
  }

  public getJustificationPolicy() {
    return this.clientConfig.config.travels.justificationPolicy;
  }

  public getBookingPhoneConfig() {
    return this.clientConfig.bookingPhoneConfig;
  }

  public getApprovalPaySelection() {
    return this.clientConfig.approvalPaySelection;
  }

  public getCorpIndividualCardsRegisterSettings() {
    return this.getCorpIndividualCards().register_settings;
  }

  public getCorpIndividualCards() {
    return this.clientConfig.corpIndividualCards;
  }

  public isCorpIndividualCardsRegisterEnabled() {
    return (
      this.clientConfig.corpIndividualCards.register_settings.status ===
      CorpIndividualSettingsStatus.ENABLED
    );
  }

  public isApprovalPaySelectionPreRequestMandatory() {
    return (
      this.clientConfig.approvalPaySelection.pre_request ===
      PreRequest.MANDATORY
    );
  }

  public isApprovalPaySelectionPostRequestAllowed() {
    return (
      this.clientConfig.approvalPaySelection.post_request ===
      PostRequest.ALLOWED
    );
  }

  public isApprovalPaySelectionPostRequestNotAllowed() {
    return (
      this.clientConfig.approvalPaySelection.post_request ===
      PostRequest.NOT_ALLOWED
    );
  }

  public getCompanyAreaDisplay() {
    return this.clientConfig.companyAreaDisplay;
  }

  public getCompanyBoardDisplay() {
    return this.clientConfig.companyBoardDisplay;
  }

  public getCustomCorporativeCardName() {
    return this.clientConfig.customCorporativeCardName;
  }

  private getCustomMessage() {
    return this.clientConfig.customMessage;
  }

  public getAgencyEmail() {
    return this.clientConfig.agencyEmail;
  }

  public getParsedCustomMessage() {
    return this.parseCustomMessage(this.getCustomMessage());
  }

  public getFlightAllowedCabinClasses() {
    return this.clientConfig.config.flights.allowedClasses;
  }

  public getHomeWarning() {
    return this.clientConfig.config.travels.homePageWarning;
  }

  public getProjectEnablingState() {
    return this.clientConfig.projectEnablingState;
  }

  public getSupportChannel() {
    return this.clientConfig.supportChannel;
  }

  public getToken() {
    return this.clientConfig.clientConfigToken;
  }

  public hasCustomMessage() {
    return !!this.clientConfig.customMessage;
  }

  public hasHomeWarning() {
    return !!this.clientConfig.config.travels.homePageWarning;
  }

  public isAnalyticsVisible() {
    return this.clientConfig.featureVisibilities.analytics;
  }

  public isEventsVisible() {
    return this.clientConfig.featureVisibilities.events;
  }

  public isExpensesVisible() {
    return this.clientConfig.featureVisibilities.expenses;
  }

  public isBusesVisible() {
    return this.clientConfig.featureVisibilities.search.bus;
  }

  public isCarsVisible() {
    return this.clientConfig.featureVisibilities.search.car;
  }

  public isHotelsVisible() {
    return this.clientConfig.featureVisibilities.search.hotel;
  }

  public isFlightsVisible() {
    return this.clientConfig.featureVisibilities.search.flight;
  }

  public getFeatureVisibilities() {
    return this.clientConfig.featureVisibilities;
  }

  public isCompanyAreaEnabled() {
    return this.clientConfig.companyAreaEnabled;
  }

  public isCompanyBudgetsEnabled() {
    return this.clientConfig.budgetsActive;
  }

  public isCompanyCostCenterEnabled() {
    return this.clientConfig.costCenterActive;
  }

  public isCompanyProjectEnabled() {
    return this.getProjectEnablingState() !== "off";
  }

  private parseCustomMessage(customMessage: string | undefined) {
    if (!customMessage) {
      return null;
    }

    try {
      const parsedCustomMessage = JSON.parse(customMessage);

      if (!parsedCustomMessage) {
        return [
          {
            children: [
              {
                text: customMessage,
              },
            ],
            type: "paragraph",
          },
        ] as string & Descendant[];
      }

      return parsedCustomMessage;
    } catch (error) {
      logger.error(`error parsing custom message: ${error}`);

      return [
        {
          children: [
            {
              text: customMessage,
            },
          ],
          type: "paragraph",
        },
      ] as string & Descendant[];
    }
  }

  public tagsAreCreatedByAdminsOnly() {
    return this.clientConfig.tagsAdminOnly;
  }

  private isNavanApprovalFlow(): boolean {
    return (
      this.clientConfig.approvalConfig.type ===
      ClientApprovalConfigType.NAVAN_SINGLE_APPROVER
    );
  }

  private isBossApprovalFlow(): boolean {
    return (
      this.clientConfig.approvalConfig.type ===
      ClientApprovalConfigType.BOSS_FLOW
    );
  }
}

export class ClientConfigModelFactory {
  static create(clientConfig: ClientConfig) {
    return new ClientConfigModel({
      ...clientConfig,
    });
  }
}

export type ClientInfo = {
  activationStatus: ClientActivationStatus;
  bookingFeeOption: BookingFeeOptions;
  budgetsActive: boolean;
  clientSlug: string;
  clientToken: string;
  costCenterActive: boolean;
  createdAt: string;
  email: string;
  id: string;
  name: string;
};

export enum GuestApprovalType {
  BOOKER_FLOW = "BOOKER_FLOW",
  REGULAR = "REGULAR",
}
